import React, { useState, useEffect } from 'react';
import { productsApi } from '../lib/api';
import { Plus, Edit2, Trash2, X, Save } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

export default function AdminProductsPage() {
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    name_en: '',
    description: '',
    description_en: '',
    price: '',
    category: 'uomo',
    subcategory: '',
    sizes: '',
    colors: '',
    images: '',
    stock: '',
    featured: false,
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await productsApi.getAll({ limit: 100, include_preloved: true });
      setProducts(response.data.products);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
        sizes: formData.sizes.split(',').map(s => s.trim()).filter(Boolean),
        colors: formData.colors.split(',').map(c => c.trim()).filter(Boolean),
        images: formData.images.split(',').map(i => i.trim()).filter(Boolean),
      };

      if (editingProduct) {
        await productsApi.update(editingProduct.product_id, data);
        toast({ title: 'Prodotto aggiornato' });
      } else {
        await productsApi.create(data);
        toast({ title: 'Prodotto creato' });
      }

      setShowForm(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast({ title: 'Errore', variant: 'destructive' });
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      name_en: product.name_en || '',
      description: product.description,
      description_en: product.description_en || '',
      price: product.price.toString(),
      category: product.category,
      subcategory: product.subcategory || '',
      sizes: product.sizes?.join(', ') || '',
      colors: product.colors?.join(', ') || '',
      images: product.images?.join(', ') || '',
      stock: product.stock?.toString() || '0',
      featured: product.featured || false,
    });
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo prodotto?')) return;
    try {
      await productsApi.delete(productId);
      toast({ title: 'Prodotto eliminato' });
      fetchProducts();
    } catch (error) {
      toast({ title: 'Errore', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      name_en: '',
      description: '',
      description_en: '',
      price: '',
      category: 'uomo',
      subcategory: '',
      sizes: '',
      colors: '',
      images: '',
      stock: '',
      featured: false,
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-light">Gestione Prodotti</h2>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingProduct(null);
            resetForm();
          }}
          data-testid="add-product-btn"
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Nuovo Prodotto
        </button>
      </div>

      {/* Product Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-medium">
                {editingProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-[#57534E] hover:text-[#1C1917]">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nome *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="product-name-input"
                    className="w-full px-3 py-2 border border-[#E5E0D8]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nome (Inglese)</label>
                  <input
                    type="text"
                    value={formData.name_en}
                    onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                    data-testid="product-name-en-input"
                    className="w-full px-3 py-2 border border-[#E5E0D8]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Prezzo (€) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                    data-testid="product-price-input"
                    className="w-full px-3 py-2 border border-[#E5E0D8]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Descrizione *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                  data-testid="product-description-input"
                  className="w-full px-3 py-2 border border-[#E5E0D8]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Descrizione (Inglese)</label>
                <textarea
                  value={formData.description_en}
                  onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                  rows={3}
                  data-testid="product-description-en-input"
                  className="w-full px-3 py-2 border border-[#E5E0D8]"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Categoria *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    data-testid="product-category-select"
                    className="w-full px-3 py-2 border border-[#E5E0D8]"
                  >
                    <option value="uomo">Uomo</option>
                    <option value="donna">Donna</option>
                    <option value="accessori">Accessori</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Sottocategoria</label>
                  <input
                    type="text"
                    value={formData.subcategory}
                    onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                    placeholder="es. camicie, pantaloni"
                    className="w-full px-3 py-2 border border-[#E5E0D8]"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Taglie (separate da virgola)</label>
                  <input
                    type="text"
                    value={formData.sizes}
                    onChange={(e) => setFormData({ ...formData, sizes: e.target.value })}
                    placeholder="S, M, L, XL"
                    className="w-full px-3 py-2 border border-[#E5E0D8]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Colori (separati da virgola)</label>
                  <input
                    type="text"
                    value={formData.colors}
                    onChange={(e) => setFormData({ ...formData, colors: e.target.value })}
                    placeholder="Bianco, Nero, Blu"
                    className="w-full px-3 py-2 border border-[#E5E0D8]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">URL Immagini (separate da virgola)</label>
                <textarea
                  value={formData.images}
                  onChange={(e) => setFormData({ ...formData, images: e.target.value })}
                  placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
                  rows={2}
                  className="w-full px-3 py-2 border border-[#E5E0D8]"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Stock *</label>
                  <input
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    required
                    data-testid="product-stock-input"
                    className="w-full px-3 py-2 border border-[#E5E0D8]"
                  />
                </div>
                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer mt-6">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="w-5 h-5 accent-[#4A5D23]"
                    />
                    <span>Prodotto in Evidenza</span>
                  </label>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  data-testid="save-product-btn"
                  className="flex-1 btn-primary flex items-center justify-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  {editingProduct ? 'Aggiorna' : 'Crea Prodotto'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-3 border border-[#E5E0D8] hover:border-[#1C1917] transition-colors"
                >
                  Annulla
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Products List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 bg-[#E5E0D8] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="bg-[#F2EFE9] overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E5E0D8]">
                <th className="text-left p-4 font-medium">Prodotto</th>
                <th className="text-left p-4 font-medium">Categoria</th>
                <th className="text-left p-4 font-medium">Prezzo</th>
                <th className="text-left p-4 font-medium">Stock</th>
                <th className="text-left p-4 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product, idx) => (
                <tr key={product.product_id} className="border-b border-[#E5E0D8]" data-testid={`product-row-${idx}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={product.images?.[0] || 'https://via.placeholder.com/50x60'}
                        alt={product.name}
                        className="w-12 h-16 object-cover"
                      />
                      <div>
                        <p className="font-medium">
                          {product.name}
                          {product.preloved && (
                            <span className="ml-2 bg-[#4A5D23] text-white text-[10px] px-2 py-0.5 uppercase tracking-wider align-middle">
                              Pre-Loved
                            </span>
                          )}
                        </p>
                        {product.featured && (
                          <span className="text-xs bg-[#4A5D23] text-white px-2 py-0.5">In Evidenza</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 capitalize">{product.category}</td>
                  <td className="p-4">€{product.price?.toFixed(2)}</td>
                  <td className="p-4">
                    <span className={product.stock < 10 ? 'text-[#C05746]' : ''}>
                      {product.stock}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(product)}
                        data-testid={`edit-product-${idx}`}
                        className="p-2 hover:bg-[#E5E0D8] transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(product.product_id)}
                        data-testid={`delete-product-${idx}`}
                        className="p-2 hover:bg-red-50 text-[#C05746] transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
